import { logger as baseLogger } from "matrix-js-sdk/src/logger";
import axios, { AxiosInstance, AxiosResponse } from "axios";

import { EnvironmentEnum, getCurrentEnvironment } from "../utils";
import { PLATFORM_UPPERCASE, sha256Hex } from "./utils/utils";
import HttpResponseError from "./errors/HttpResponseError";
import { _t } from "../../languageHandler";
import { IDthFetchDomainInfoRequest, IDthLicenseJwtResponse, IDthValidateLicenseRequest } from "./models/httpClientLicenseModels";
import { GenericResponse } from "./models/httpClientModels";
import { APP_VERSION } from "../constants";

const logger = baseLogger.getChild("DthHttpLicenseClient");


interface DthHttpLicenseClientOptions {
    baseUrl: string;
}

export class DthHttpLicenseClient {
    private static instance: DthHttpLicenseClient | null = null;
    private static baseUrl: string | null = null;  // URL statico per inizializzazione
    private axiosClient: AxiosInstance;
    private opts: DthHttpLicenseClientOptions;

    /**
     * Costruttore privato, per forzare l'uso del singleton.
     * Verifica che i parametri statici siano stati inizializzati.
     * Lancia un errore se si tenta di creare un'istanza senza inizializzazione o se i parametri non sono validi.
     */
    private constructor() {
        if (!DthHttpLicenseClient.baseUrl) {
            throw new Error(_t("http_client_errors.parameters_must_be_set_before_creating_the_instance"));
        }
        this.opts = {
            baseUrl: DthHttpLicenseClient.baseUrl,
        };

        this.axiosClient = axios.create({
            baseURL: this.opts.baseUrl,
            timeout: 30000,
            validateStatus: () => true //le eccezioni sugli stati http vengono gestite dall'interceptor
        });

        const environment: EnvironmentEnum = getCurrentEnvironment();

        // Interceptor per aggiungere header comuni a tutte le richieste
        this.axiosClient.interceptors.request.use(
            (config) => {
                const build = "1";
                const version = APP_VERSION;

                // config.headers["User-Agent"] = `GOT/0.4.15-dev (Build 102; sdk_gphone64_arm64 Google; Android 15)`;
                config.headers["Content-Type"] = "application/json";
                config.headers["X-Client-Type"] = "GOT Web";
                config.headers["X-Client-Version"] = `${version}(${build})`;
                config.headers["Environment"] = environment;
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Interceptor per gestire in modo centralizzato le eccezioni dovuto allo status code della risposta
        this.axiosClient.interceptors.response.use(response => {
            if (response.status >= 200 && response.status <= 299)
                return response;

            let responseErrorText =
                response.data?.error?.message ??
                response.data?.error ??
                response.data ??
                _t("settings.general.generic_error_http", {
                    errorText: response.statusText,
                    httpStatus: response.status,
                });

            if (typeof responseErrorText !== "string") {
                try {
                    responseErrorText = JSON.stringify(responseErrorText);
                } catch (e) {
                    responseErrorText = _t("settings.general.generic_error_http", {
                        errorText: response.statusText,
                        httpStatus: response.status,
                    }); // fallback di sicurezza
                }
            }
            logger.error("Error message: " + responseErrorText)

            throw new HttpResponseError(response.status, responseErrorText, response);
        });

    }

    /**
     * Inizializza i parametri statici necessari per il funzionamento del DthHttpLicenseClient. 
     * Deve essere chiamato prima di ottenere l'istanza.
     * @param options Parametri di inizializzazione
     */
    public static initialize(options: DthHttpLicenseClientOptions): void {
        if (DthHttpLicenseClient.baseUrl) {
            throw new Error(_t("http_client_errors.license_client_already_initialized"));
        }

        // Rimuove eventuali slash finali dall'URL
        DthHttpLicenseClient.baseUrl = options.baseUrl;
    }

    /**
     * Metodo statico per ottenere l'istanza del DthHttpLicenseClient.
     * Se l'istanza non esiste, viene creata.
     * @returns L'istanza del DthHttpLicenseClient
     */
    public static getInstance(): DthHttpLicenseClient {
        if (!DthHttpLicenseClient.instance) {
            DthHttpLicenseClient.instance = new DthHttpLicenseClient();
        }
        return DthHttpLicenseClient.instance;
    }

    /**
     * Recupera le info dominio licenza
     */
    public async fetchDomainInfo(props: IDthFetchDomainInfoRequest)
        : Promise<IDthLicenseJwtResponse> {
        const { bundleId, domain } = props;
        // Hash del dominio per l'endpoint, rimuovendo eventuali slash finali prima di hashare
        const hashedDomain = await sha256Hex(domain.replace(/\/+$/, ""));
        const url = `/${hashedDomain}/discovery`;
        try {
            const res: AxiosResponse = await this.axiosClient.post(url, {
                bundle_id: bundleId,
                platform: PLATFORM_UPPERCASE
            });
            const resData = res.data as GenericResponse<string>;
            if (!resData.success || resData.error) {
                throw new HttpResponseError(
                    res.status,
                    _t("http_client_errors.fetch_domain_info_failed") + ": " + JSON.stringify(resData.error),
                    res
                );
            }
            return { jwt: resData.data } as IDthLicenseJwtResponse;
        } catch (err) {
            throw err;
        }
    }

    /**
     * Valida la licenza e recupera il JWT
     * @param props IDthValidateLicenseRequest
     * @returns IDthLicenseJwtResponse
     */
    public async validateLicense(props: IDthValidateLicenseRequest)
        : Promise<IDthLicenseJwtResponse> {
        const { bundleId, domain, userId, telemetryToken } = props;
        // Hash del dominio per l'endpoint, rimuovendo eventuali slash finali prima di hashare
        const hashedDomain = await sha256Hex(domain.replace(/\/+$/, ""));
        const url = `/${hashedDomain}`;
        try {
            const res: AxiosResponse = await this.axiosClient.post(url, {
                user_id: userId,
                bundle_id: bundleId,
                platform: PLATFORM_UPPERCASE,
                telemetry_token: telemetryToken
            });
            const resData = res.data as GenericResponse<string>;
            if (!resData.success || resData.error) {
                throw new HttpResponseError(
                    res.status,
                    _t("http_client_errors.validate_license_failed") + ": " + JSON.stringify(resData.error),
                    res
                );
            }
            return { jwt: resData.data } as IDthLicenseJwtResponse;
        } catch (err) {
            throw err;
        }
    }
}

